import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone, ExternalLink, Clock, Search, Shield, HeartPulse, MapPin } from 'lucide-react';
import { SUPPORT_ORGS, DA_ORGS, SV_ORG_IDS } from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';

const SCOTLAND_ORGS = [
  {
    id: 'rape-crisis-scotland',
    name: 'Rape Crisis Scotland',
    tagline: 'Support after any experience of sexual violence, in Scotland.',
    description:
      "Free, confidential support. National Helpline runs every day 5pm–midnight. Advocacy workers can help you through Police Scotland, COPFS and court.",
    phone: '08088010302',
    phoneLabel: 'Call 08088 01 03 02',
    website: 'https://www.rapecrisisscotland.org.uk',
    hours: 'Daily 5pm–midnight',
    category: 'specialist',
    topic: 'sv',
  },
  {
    id: 'sdafmh',
    name: "Scotland's Domestic Abuse & Forced Marriage Helpline",
    tagline: '24/7 support for anyone affected by domestic abuse in Scotland.',
    description:
      'Free confidential helpline for women, men and non-binary survivors, families and friends. Run by Scottish Women\u2019s Aid.',
    phone: '08000271234',
    phoneLabel: 'Call 0800 027 1234',
    website: 'https://sdafmh.org.uk',
    hours: '24/7',
    category: 'helpline',
    topic: 'da',
  },
  {
    id: 'scottish-womens-aid',
    name: "Scottish Women's Aid",
    tagline: 'National network of local Women\u2019s Aid services across Scotland.',
    description:
      'Refuge, advocacy and support for women and children experiencing domestic abuse. Local services in every part of Scotland.',
    website: 'https://womensaid.scot',
    hours: 'Office hours + local services',
    category: 'specialist',
    topic: 'da',
  },
  {
    id: 'amis',
    name: 'Abused Men in Scotland (AMIS)',
    tagline: 'Domestic abuse helpline for men in Scotland.',
    description:
      'Free, confidential helpline and one-to-one support for male survivors of domestic abuse in Scotland.',
    phone: '08088000024',
    phoneLabel: 'Call 0808 800 0024',
    website: 'https://www.amis.org.uk',
    hours: 'Mon–Fri office hours',
    category: 'helpline',
    topic: 'da',
  },
  {
    id: 'lgbt-helpline-scotland',
    name: 'LGBT Helpline Scotland',
    tagline: 'Support for LGBT+ survivors in Scotland.',
    description:
      'Confidential emotional and practical support for LGBT+ people in Scotland, including anyone affected by domestic abuse or sexual violence.',
    phone: '03001232523',
    phoneLabel: 'Call 0300 123 2523',
    website: 'https://www.lgbthealth.org.uk/services-support/helpline/',
    hours: 'See website',
    category: 'specialist',
    topic: 'sv',
  },
  {
    id: 'archway-sarc',
    name: 'Archway SARC (Glasgow)',
    tagline: "Scotland's specialist sexual-assault referral service, based in Glasgow.",
    description:
      'Forensic medical services, aftercare and referral to specialist support. Open 24/7 for anyone in the West of Scotland — call 111 out-of-hours to be referred.',
    phone: '01412118175',
    phoneLabel: 'Call 0141 211 8175',
    website: 'https://www.archway.sartsupport.scot.nhs.uk',
    hours: '24/7 (via 111 out-of-hours)',
    category: 'medical',
    topic: 'sv',
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'emergency', label: 'Emergency' },
  { id: 'helpline', label: 'Helpline' },
  { id: 'specialist', label: 'Specialist' },
  { id: 'medical', label: 'Medical' },
];

const SV_ORGS = SUPPORT_ORGS.filter((o) => SV_ORG_IDS.includes(o.id));

const TABS = [
  {
    id: 'sv',
    label: 'Sexual violence',
    icon: HeartPulse,
    intro:
      "Free, confidential UK services for anyone affected by rape or sexual violence \u2014 you don\u2019t need to have reported to access any of these.",
    orgs: SV_ORGS,
  },
  {
    id: 'da',
    label: 'Domestic abuse',
    icon: Shield,
    intro:
      "Free, confidential UK services for anyone experiencing or worried about domestic abuse \u2014 including coercive control, financial, emotional, \u201chonour\u201d-based and same-sex relationship abuse.",
    orgs: DA_ORGS,
  },
];

function OrgCard({ o }) {
  return (
    <div className="tf-card p-5" data-testid={`org-${o.id}`}>
      <div className="flex items-start gap-3">
        <div
          className="px-2 py-0.5 rounded-full text-[10px] font-bold"
          style={{
            background:
              o.category === 'emergency'
                ? '#F2DDE2'
                : o.category === 'helpline'
                ? '#D8E2EE'
                : o.category === 'specialist'
                ? 'var(--tf-purple-light)'
                : '#E6E0EE',
            color: 'var(--tf-purple-dark)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {o.category}
        </div>
        {o.hours && (
          <div
            className="ml-auto flex items-center gap-1"
            style={{ color: 'var(--tf-text-muted)', fontSize: 11 }}
          >
            <Clock size={11} />
            <span className="font-body">{o.hours}</span>
          </div>
        )}
      </div>

      <h3
        className="font-heading mt-3"
        style={{ fontSize: 18.5, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        {o.name}
      </h3>
      <p
        className="font-body"
        style={{ fontSize: 12.5, color: 'var(--tf-purple)', fontWeight: 700, marginTop: 2 }}
      >
        {o.tagline}
      </p>
      <p
        className="font-body mt-3"
        style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
      >
        {o.description}
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        {o.phone && (
          <a
            href={`tel:${o.phone}`}
            data-testid={`call-${o.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold"
            style={{
              background: 'var(--tf-purple)',
              color: '#fff',
              fontSize: 12.5,
              textDecoration: 'none',
            }}
          >
            <Phone size={13} />
            {o.phoneLabel || `Call ${o.phone}`}
          </a>
        )}
        {o.website && (
          <a
            href={o.website}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`web-${o.id}`}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-bold"
            style={{
              background: '#fff',
              color: 'var(--tf-purple)',
              fontSize: 12.5,
              border: '1px solid var(--tf-purple-light)',
              textDecoration: 'none',
            }}
          >
            <ExternalLink size={13} />
            Visit website
          </a>
        )}
      </div>
    </div>
  );
}

export default function Support() {
  const location = useLocation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');

  // Tab is derived directly from the URL (?tab=sv|da). Default sv.
  const urlTab = new URLSearchParams(location.search).get('tab');
  const tabId = urlTab === 'da' ? 'da' : 'sv';
  const setTabId = (id) => {
    navigate(`/app/support?tab=${id}`, { replace: false });
    setCat('all');
  };

  const tab = TABS.find((t) => t.id === tabId) || TABS[0];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return tab.orgs.filter((o) => {
      if (cat !== 'all' && o.category !== cat) return false;
      if (!needle) return true;
      return (
        o.name.toLowerCase().includes(needle) ||
        o.tagline.toLowerCase().includes(needle) ||
        o.description.toLowerCase().includes(needle)
      );
    });
  }, [q, cat, tab]);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="support-screen">
      <SEO
        title="UK Support Organisations — Sexual Violence & Domestic Abuse"
        description="Free confidential UK helplines for survivors of sexual violence and domestic abuse — for women, men, non-binary and LGBT+. Includes Rape Crisis, Refuge, Women's Aid, SurvivorsUK, Mankind Initiative, Men's Advice Line, Survivors Trust, Samaritans, SARCs, Galop and NCDV."
        path="/app/support"
      />
      <p className="eyebrow">SUPPORT ORGANISATIONS · ENGLAND, WALES &amp; SCOTLAND</p>
      <h1
        className="font-heading mt-3"
        style={{
          fontSize: 'clamp(28px, 4vw, 36px)',
          lineHeight: 1.15,
          color: 'var(--tf-text)',
          letterSpacing: '-0.01em',
        }}
      >
        Free, confidential help across the UK.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        Choose the topic that fits best — sexual violence services on one tab, domestic abuse
        on the other. Tap to call or visit a website. Scottish specialist services are listed
        in a dedicated section at the bottom of each tab —{' '}
        <a href="#scotland" className="tf-link" data-testid="support-jump-scotland">jump to Scotland</a>.
      </p>

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      {/* Topic tabs */}
      <div
        className="mt-7 grid grid-cols-2 gap-2 p-1.5 rounded-2xl tf-no-print"
        style={{ background: 'var(--tf-bg-secondary)', border: '1px solid var(--tf-card-border)' }}
        role="tablist"
      >
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tabId === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                setTabId(t.id);
              }}
              data-testid={`tab-support-${t.id}`}
              className="inline-flex items-center justify-center gap-2 py-2.5 rounded-xl font-bold transition-all"
              style={{
                background: active ? '#fff' : 'transparent',
                color: active ? 'var(--tf-purple)' : 'var(--tf-text-secondary)',
                boxShadow: active ? '0 4px 14px rgba(139,123,177,0.15)' : 'none',
                border: 'none',
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              <Icon size={15} strokeWidth={active ? 2.2 : 1.8} />
              {t.label}
            </button>
          );
        })}
      </div>

      <p
        className="font-body mt-5"
        style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
      >
        {tab.intro}
      </p>

      {/* Search + chips */}
      <div className="mt-5 flex flex-col gap-3 tf-no-print">
        <div
          className="flex items-center gap-2 px-4 py-3 rounded-full"
          style={{ background: '#fff', border: '1px solid var(--tf-card-border)' }}
        >
          <Search size={16} color="var(--tf-text-muted)" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${tab.label.toLowerCase()} services…`}
            data-testid="support-search"
            className="flex-1 outline-none bg-transparent font-body"
            style={{ fontSize: 14, color: 'var(--tf-text)' }}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((c) => {
            const active = cat === c.id;
            return (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                data-testid={`filter-${c.id}`}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-colors"
                style={{
                  background: active ? 'var(--tf-purple)' : '#fff',
                  color: active ? '#fff' : 'var(--tf-text-secondary)',
                  border: `1px solid ${active ? 'var(--tf-purple)' : 'var(--tf-card-border)'}`,
                  cursor: 'pointer',
                }}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="mt-8 flex flex-col gap-3">
        {filtered.map((o) => (
          <OrgCard key={o.id} o={o} />
        ))}
        {filtered.length === 0 && (
          <div
            className="p-8 text-center rounded-2xl"
            style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-text-muted)' }}
          >
            <p className="font-body" style={{ fontSize: 13.5 }}>
              No services match your search. Try a different keyword or category.
            </p>
          </div>
        )}
      </div>

      {/* Scotland-specialist section — always visible, filtered by current tab */}
      <section
        className="mt-12"
        data-testid="support-section-scotland"
        id="scotland"
        style={{ scrollMarginTop: 96 }}
      >
        <div className="flex items-center gap-2">
          <MapPin size={14} color="var(--tf-purple-dark)" aria-hidden="true" />
          <p className="eyebrow">IN SCOTLAND — SPECIALIST HELPLINES</p>
        </div>
        <h2
          className="font-heading mt-2"
          style={{
            fontSize: 24,
            color: 'var(--tf-text)',
            letterSpacing: '-0.01em',
          }}
        >
          If you are in Scotland
        </h2>
        <p
          className="font-body mt-3"
          style={{
            fontSize: 14,
            color: 'var(--tf-text-secondary)',
            lineHeight: 1.7,
          }}
        >
          Scotland has its own specialist support network. These services are Scotland-based,
          understand Police Scotland, COPFS and Scots law, and are free and confidential.
        </p>
        <div className="mt-5 flex flex-col gap-3">
          {SCOTLAND_ORGS.filter((o) => o.topic === tabId).map((o) => (
            <OrgCard key={o.id} o={o} />
          ))}
          {SCOTLAND_ORGS.filter((o) => o.topic === tabId).length === 0 && (
            <div
              className="p-6 rounded-2xl"
              style={{ background: 'var(--tf-bg-secondary)', color: 'var(--tf-text-muted)' }}
            >
              <p className="font-body" style={{ fontSize: 13 }}>
                Switch tab above to see Scottish {tabId === 'sv' ? 'domestic abuse' : 'sexual violence'} services too.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
